/**
 * Analytics Core Dispatcher - Transport Layer
 *
 * Routes analytics events through the consent-aware manager.
 * Events will be queued until Didomi consent is resolved.
 */

import debounce from 'lodash/debounce';

import type { AnalyticsPayload } from './types';

// =============================================================================
// Global Type Extensions
// =============================================================================

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dataLayer: any[];
        consentDataLayer: {
            queueOrPush: (event: unknown) => void;
            getQueueSize: () => number;
            isReady: () => boolean;
        };
        _consentDataLayerBootstrap: unknown[];
        triggerAddToCart: (isMobileEvent?: boolean) => void;
    }
}

// =============================================================================
// Configuration
// =============================================================================

/**
 * Default debounce delay for frequent events (in milliseconds).
 */
export const DEBOUNCE_DELAY_MS = 500;

// =============================================================================
// Core Dispatcher Functions
// =============================================================================

/**
 * Pushes an analytics event through the consent manager.
 * Events will be queued until Didomi consent is resolved.
 *
 * @param payload - The analytics payload to push
 */
export function pushToDataLayer<T = unknown>(
    payload: AnalyticsPayload<T>,
): void {
    if (typeof window === 'undefined') {
        return;
    }

    if (!window.consentDataLayer) {
        console.error(
            '[Analytics] Consent manager not available. Ensure consent-datalayer-manager.js is loaded.',
        );
        return;
    }

    console.debug('[Analytics] Dispatching event:', payload.event, payload);
    window.consentDataLayer.queueOrPush(payload);
}

// =============================================================================
// Debounced Push Function
// =============================================================================

export const pushToDataLayerDebounced = debounce(
    <T>(payload: AnalyticsPayload<T>) => {
        pushToDataLayer(payload);
    },
    DEBOUNCE_DELAY_MS,
);

// =============================================================================
// Parsing Utilities
// =============================================================================

/**
 * Parses a JSON analytics context string from PHP.
 *
 * @param contextJson - JSON string from PHP BookingFlowAnalyticsService
 * @returns Parsed payload or null if invalid
 */
export function parseAnalyticsContext<T = AnalyticsPayload>(
    contextJson: string,
): T | null {
    if (!contextJson || contextJson.trim() === '') {
        return null;
    }

    try {
        return JSON.parse(contextJson) as T;
    } catch {
        console.error('[Analytics] Failed to parse analytics context');
        return null;
    }
}

// =============================================================================
// Debug Helpers
// =============================================================================

/**
 * Get current queue size (for debugging).
 *
 * @returns Number of queued events
 */
export function getQueueSize(): number {
    if (typeof window !== 'undefined' && window.consentDataLayer) {
        return window.consentDataLayer.getQueueSize();
    }
    return 0;
}

/**
 * Check if Didomi is ready (for debugging).
 *
 * @returns true if Didomi ready, false otherwise
 */
export function isDidomiReady(): boolean {
    if (typeof window !== 'undefined' && window.consentDataLayer) {
        return window.consentDataLayer.isReady();
    }
    return false;
}
